import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// True on operations-test.*. Test env auto-reloads on new bundle so kiosk
// iPads don't need a manual refresh to pick up my in-progress fixes.
const IS_TEST_ENV = (() => {
    try {
        return /(^|\.)operations-test\./i.test(window.location.hostname);
    } catch { return false; }
})();

// Suppress the banner whenever the operator has a picking/pressing/packing
// order detail open — we don't want to overlay a "reload" prompt on top of
// a kiosk mid-scan. Matches both the fullscreen kiosk URL shape
// (/picking-portal/active/orders/...) and the sidebar shape
// (/store/<slug>/picking-portal/orders/...). The banner re-evaluates on
// every route change, so it appears the moment the operator returns to a
// search/landing screen.
const PORTAL_ORDER_DETAIL_RX =
    /^\/(picking|pressing|packing)-portal\/active\/orders\/|\/store\/[^/]+\/(picking|pressing|packing)-portal\/orders\//;

// Read the main.HASH.js filename currently loaded in this tab. The browser
// records every fetched script under Performance Resource Timing, so we look
// there first; if that's empty (rare — e.g. very old browsers), we fall back
// to scanning <script> tags. Returns null when no match.
function getLoadedBundleName() {
    try {
        const entries = performance.getEntriesByType?.('resource') || [];
        for (const e of entries) {
            const m = (e.name || '').match(/main\.[a-f0-9]+\.js/);
            if (m) return m[0];
        }
    } catch { /* ignore */ }
    try {
        const scripts = document.querySelectorAll('script[src*="/static/js/main."]');
        for (const s of scripts) {
            const m = (s.src || '').match(/main\.[a-f0-9]+\.js/);
            if (m) return m[0];
        }
    } catch { /* ignore */ }
    return null;
}

// Poll /api/version every minute. When the server's bundle filename diverges
// from the one this tab loaded, surface a sticky banner inviting the
// operator to reload. The banner is dismissible — a stubborn operator who
// closes it can keep working — but it reappears on the next poll if they
// don't reload.
export default function AppVersionBanner() {
    const [serverBundle, setServerBundle] = useState(null);
    const [dismissed,    setDismissed]    = useState(false);
    const loadedBundleRef = useRef(getLoadedBundleName());
    const location = useLocation();
    const inPortalOrder = PORTAL_ORDER_DETAIL_RX.test(location.pathname);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                const res = await fetch('/api/version', { cache: 'no-store' });
                if (!res.ok) return;
                const j = await res.json();
                if (!cancelled && j && j.bundle) setServerBundle(j.bundle);
            } catch { /* network error; try again next tick */ }
        };
        check(); // initial
        const id = setInterval(check, 60 * 1000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Reset the dismiss state if the server publishes ANOTHER new build —
    // the operator deserves a fresh prompt for each version they're behind.
    const lastSeenServerRef = useRef(null);
    useEffect(() => {
        if (serverBundle && serverBundle !== lastSeenServerRef.current) {
            lastSeenServerRef.current = serverBundle;
            setDismissed(false);
        }
    }, [serverBundle]);

    const loaded = loadedBundleRef.current;
    const outOfDate = loaded && serverBundle && loaded !== serverBundle;

    // 2026-08-04: auto-reload on prod too (was test-only) — with the same
    // "not mid-order" gate below so a packer / picker / presser mid-scan
    // is never interrupted. Prior prod behavior required the operator to
    // tap the banner Reload button, which left stale bundles running for
    // hours on the shop floor after a deploy.
    // Test env still fires unconditionally (even mid-order) so dev
    // iteration doesn't need a manual tap.
    useEffect(() => {
        if (!outOfDate) return;
        if (!IS_TEST_ENV && inPortalOrder) return; // prod safety gate
        const t = setTimeout(() => {
            try { window.location.reload(); } catch { /* ignore */ }
        }, 3000);
        return () => clearTimeout(t);
    }, [outOfDate, inPortalOrder]);

    // 2026-08-27: the prod safety gate above kept operators who live INSIDE
    // portal order pages stale across an entire shift (John C.: ~10 deploys
    // in one day, chunk files rotated out of the A/B slots, his tap into the
    // packing order page died client-side with no backend request ever
    // logged). A route CHANGE is a safe interruption point — nothing is
    // mid-scan during navigation — so when we're out of date, reload on the
    // very next navigation, order page or not.
    const prevPathRef = useRef(location.pathname);
    useEffect(() => {
        const prev = prevPathRef.current;
        prevPathRef.current = location.pathname;
        if (!outOfDate || prev === location.pathname) return;
        try { window.location.reload(); } catch { /* ignore */ }
    }, [location.pathname, outOfDate]);

    // Hide while an order is open in any portal (prod only). On test, we
    // still render so the operator sees the 3s countdown, then auto-reload.
    if (!outOfDate || dismissed || (inPortalOrder && !IS_TEST_ENV)) return null;

    const handleReload = () => {
        try { window.location.reload(); } catch { /* ignore */ }
    };

    return (
        <div
            role="status"
            style={{
                position: 'fixed',
                top: 12, left: 12, right: 12,
                zIndex: 99999,
                background: '#1a2744',
                color: '#fff',
                borderRadius: 10,
                padding: '12px 16px',
                boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                fontSize: 14,
                fontWeight: 500,
            }}
        >
            <span style={{ flex: 1 }}>
                A new version of Operations Hub is available.
                {IS_TEST_ENV && ' Auto-reloading…'}
            </span>
            <button
                onClick={handleReload}
                style={{
                    background: '#27ae60',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 14px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                }}
            >
                Reload
            </button>
            <button
                onClick={() => setDismissed(true)}
                aria-label="Dismiss"
                style={{
                    background: 'transparent',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: 6,
                    padding: '8px 10px',
                    fontSize: 13,
                    cursor: 'pointer',
                }}
            >
                Later
            </button>
        </div>
    );
}
